import { dateStrToAgeDays, dateStrToAgeMonths, Enum_Patientvisit_Status, Enum_Survey_Access_Restriction, notEmpty, PatientDobFormat, useGenerateDescriptiveReportMutation } from '@cyren/common-lib';
import classNames from 'classnames';
import { memo, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useStaticLocales } from '../../admin/hooks/locales/use-static-locale';
import Footer from '../../app/Footer';
import thumbsUp from '../../assets/ill/Thumbs_up.svg';
import LoadingAnim from '../../components/LoadingAnim';
import LogoPatientDynamic from '../../components/LogoPatientDynamic';
import PatientOrgContainer from '../hooks/PatientOrgContainer';
import { useOnNavChange } from '../../hooks/use-on-nav-change';
import { useData } from '../../report/use-data';
import { usePatientTransData } from '../../report/use-patient-trans-data';
import { useSurveyMeta } from '../../survey/use-survey-meta';
import { useTranslations } from '../hooks/use-translations';
import PaTreeHeader from '../tree/PaTreeHeader';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router';
import { useRecoilState } from 'recoil';
import { PaReportState } from '../hooks/use-report';
import { TOptions } from '../pa-prop-types';
import { AthenaServices } from '../../lib/athena/athena-services';
import { useSurveyState, usePaSurveyParams } from '../hooks/use-survey-state';

const collapCls =
  'collapse collapse-arrow border-2 border-black border-opacity-10 rounded-none first:rounded-t-xl last:rounded-b-xl border-t-0 first:border-t-2 font-base';
const collapHeaderCls = 'collapse-title text-base font-medium';
const collapBodyCls = 'collapse-content opacity-70 text-base';

function PaSurveyEndPageContent() {
  const [
    {
      surveyId,
      dataState: { survey, visit },
    },
  ] = useData({ disableRedirectOnInvalidReport: true });

  const nav = useNavigate();
  const { orgId, shortId } = usePaSurveyParams();
  const baseUrl = `/s/${orgId}/${shortId}`;
  const { sltStr } = useStaticLocales();
  const [, { t }] = useTranslations();
  const container = PatientOrgContainer.useContainer();

  const [allowNavigate, setAllowNavigate] = useState(false);
  const [selectedFaqIndex, setSelectedFaqIndex] = useState<number | null>(null);
  const [athenaIntegrationExecuted, setAthenaIntegrationExecuted] = useState(false);
  const [isAthenaProcessing, setIsAthenaProcessing] = useState(false);

  // reportState, surveyState
  const [reportState] = useRecoilState(PaReportState);
  const [{ surveyState }] = useSurveyState();

  const [ccdaTemp, setCcdaTemp] = useState<string>('');

  const [generateDescriptiveReport, { data: reportData, loading: loadingReport, error: reportError }] = useGenerateDescriptiveReportMutation({
    onError: (error) => {
      console.error('Error generating descriptive report:', error);
    },
  });

  useOnNavChange({
    block: allowNavigate,
    onChange: () => {
      toast.info(sltStr({ key: 'msg-cannot-go-back' }), {
        toastId: 'msg-cannot-go-back',
      });
    },
  });

  const { transKeys, metaItems } = useSurveyMeta({
    surveyId,
    metaKey: 'faq',
  });

  const tOptions = (reportState?.systemValueMap ? Object.fromEntries(
    Object.entries(reportState?.systemValueMap).filter(([ , value]) => typeof value === 'string'))
    : {}) as TOptions;
  const titleLabel = t('finish-page-title', tOptions, { fallbackValue: null });
  const subtitleLabel = t('finish-page-desc', tOptions, { fallbackValue: null });

  const pageKeys = survey?.metadata?.finishedPageKeys || [];
  const [{ loadingTrans }] = usePatientTransData({
    keys: [...pageKeys, ...transKeys],
  });

  // 설문 완료 페이지 접근 시 localStorage 정리
  useEffect(() => {
    // 설문 완료 페이지에 접근하면 모든 설문 관련 localStorage 정리
    try {
      // encounterCheck 제거
      localStorage.removeItem('encounterCheck');
      
      // recoil-persist로 저장된 설문 상태들도 정리
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('AdminPatientSurveyState') || key.includes('PaReportState'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
    } catch (error) {
      console.warn('localStorage 정리 중 오류:', error);
    }
  }, []);

  // 환자 방문이 완료되면 Athena check-in 실행
  useEffect(() => {
    const medication = surveyState?.medication;
    const medicationCurrent = surveyState?.currentMedication;
    const allergy = surveyState?.allergy;
    const allergyFinal = surveyState?.allergyFinal;
    const reaction = surveyState?.reaction ?? 'Other';
    const providerCCDA = surveyState?.providerCCDA;

    if (surveyState?.patientDetails && !athenaIntegrationExecuted) {
      localStorage.removeItem('encounterCheck');
      setIsAthenaProcessing(true);
      
      // Athena 통합 함수들을 순차적으로 실행
      const executeAthenaIntegration = async () => {
        try {
          // await sendDescriptiveNoteToAthena();
          // await handleAllergy();
          // await handleMedication();
          await handleProviderAndPharmacy();
          // Athena 통합 완료 후 PaSurveyTerminatedPage로 이동
          nav(`${baseUrl}/terminated`);
        } catch (error) {
          console.error('Athena 통합 중 오류 발생:', error);
          // 오류가 발생해도 terminated 페이지로 이동
          nav(`${baseUrl}/terminated`);
        } finally {
          setIsAthenaProcessing(false);
          setAthenaIntegrationExecuted(true);
        }
      };
      
      executeAthenaIntegration();
    }
  }, [surveyState?.patientDetails, athenaIntegrationExecuted]);

  const handleProviderAndPharmacy = async () => {
    const patientId = surveyState?.patientDetails?.patientid;
    const departmentId = surveyState?.patientDetails?.departmentid;
    const providerId = surveyState?.providerId;
    const pharmacyId = surveyState?.pharmacyId;
    
    if (pharmacyId) {
      await AthenaServices.putPharmacy(patientId, departmentId, pharmacyId);
    }

    if (providerId) {
      await AthenaServices.putProvider(patientId, departmentId, providerId);
    }
  }

  if (!survey || loadingTrans) {
    return (
      <div className='flex flex-1 w-full'>
        <LoadingAnim className='h-full p-10 w-full' />
      </div>
    );
  }

  return (
      <div className='flex-1 flex-col gap-5 justify-between'>
      <div className='flex-1 flex-col gap-5'>
        <PaTreeHeader showFullBar noBack />

        <div className='px-5'>
          <div className='flex-center'>
            <LogoPatientDynamic logoUrl={container.logoUrl} className='w-9' />
          </div>

          {!visit?.archived && (
            <div className='flex-center flex-col gap-4'>
                <>
                  <div className='pt-12 pb-6 w-1/3'>
                    <img src={thumbsUp} />
                  </div>

                  <div className='flex-col gap-2 flex-center-y'>
                    {(() => {
                      return (
                        <>
                          <h4 className='font-bold sm'>Thank You!</h4>
                          <div className='text-base opacity-70 px-2 text-center'>
                            Your information has been sent to your care team because your visit has started.
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </>
            </div>
          )}

          {visit?.archived && (
            <div className='flex-center flex-col gap-4 py-10'>
              <div className='flex-col gap-2 flex-center-y'>
                <div className='text-base opacity-70 px-2 text-center flex-1 bg-primary bg-opacity-10 rounded-xl w-full p-2'>
                  {sltStr({ key: 'finished-archived' })}
                </div>
              </div>
            </div>
          )}

          {survey.access_restriction && survey.access_restriction !== Enum_Survey_Access_Restriction.ProviderOnly &&
            <div className='flex-center flex-col gap-4 py-10'>
              <div className='flex-col gap-2 flex-center-y'>
                <Button variant="contained"
                  onClick={() => {
                    setAllowNavigate(true);
                    nav(`/sp/${visit?.uuid}`);
                  }}
                >View Medical Provider Report</Button>
              </div>
            </div>

          }

          {/* FAQ */}
          {notEmpty(metaItems) && (
            <div className='flex-col py-4 w-full'>
              {metaItems?.map((metaItem, idx) => {
                const open = selectedFaqIndex === idx;
                return (
                  <div
                    key={metaItem.id}
                    className={classNames(collapCls, open && 'collapse-open')}
                    onClick={() => {
                      if (open) {
                        setSelectedFaqIndex(null);
                      } else {
                        setSelectedFaqIndex(idx);
                      }
                    }}
                  >
                    <div className={collapHeaderCls}>{t(metaItem.qnaQuestionKey, tOptions)}</div>
                    <div className={collapBodyCls}>
                      <p>{t(metaItem.qnaAnswerKey, tOptions)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className='hidden flex-none'>
        <Footer />
      </div>
    </div>
  );
}

function PaSurveyEndPage() {
  return (
    <PatientOrgContainer.Provider>
      <PaSurveyEndPageContent />
    </PatientOrgContainer.Provider>
  );
}

export default memo(PaSurveyEndPage);

